/**
 * Track & Trace Design System Constants
 *
 * This file contains all color, style, and visual constants for the Track & Trace module
 * following the Field Pulse design system standards.
 */

// ============================================
// Status Badge Colors (Semantic)
// ============================================

/**
 * Every value below resolves through the status/mode token families, which
 * carry their own dark values. That is why no `dark:` variants appear here —
 * hardcoded palette classes cannot theme, and previously rendered light
 * badges on the dark canvas.
 *
 * Class names are written out in full rather than composed from a helper:
 * Tailwind's JIT scans source text, so a name built at runtime is never
 * generated and the style silently goes missing.
 */
export const SHIPMENT_STATUS_STYLES = {
  // Success states
  DELIVERED: 'bg-status-success-subtle text-status-success-foreground',

  // Active states
  BOOKED: 'bg-status-info-subtle text-status-info-foreground',
  IN_TRANSIT: 'bg-status-info-subtle text-status-info-foreground',

  // Warning states
  PENDING: 'bg-status-warning-subtle text-status-warning-foreground',

  // Error states
  DELAYED: 'bg-status-destructive-subtle text-status-destructive-foreground',
  EXCEPTION: 'bg-status-destructive-subtle text-status-destructive-foreground',
  CANCELLED: 'bg-status-destructive-subtle text-status-destructive-foreground',

  // Neutral states
  DRAFT: 'bg-status-neutral-subtle text-status-neutral-foreground',
} as const;

// ============================================
// Timeline Event Colors
// ============================================

export const TIMELINE_EVENT_COLORS = {
  // Pickup/Origin events
  PICKED_UP: 'bg-status-success',
  LOADED: 'bg-status-success',
  DEPARTED: 'bg-status-success',

  // Transit events
  IN_TRANSIT: 'bg-status-info',
  PORT_ARRIVAL: 'bg-status-info',
  PORT_DEPARTURE: 'bg-status-info',

  // Customs/Regulatory — informational milestones. These were purple, a hue
  // outside the five-tone status palette, so they now share the info tone.
  CUSTOMS_CLEARED: 'bg-status-info',
  SECURITY_CLEARED: 'bg-status-info',
  INSPECTION_COMPLETE: 'bg-status-info',

  // Delivery events
  DELIVERED: 'bg-status-success',
  POD_RECEIVED: 'bg-status-success',

  // Exception events
  EXCEPTION: 'bg-status-destructive',
  DELAYED: 'bg-status-destructive',
  DAMAGE_REPORTED: 'bg-status-destructive',

  // Default
  DEFAULT: 'bg-primary',
} as const;

export const TIMELINE_EVENT_BORDER_COLORS = {
  PICKED_UP: 'border-status-success-border bg-status-success-subtle',
  LOADED: 'border-status-success-border bg-status-success-subtle',
  DEPARTED: 'border-status-success-border bg-status-success-subtle',

  IN_TRANSIT: 'border-status-info-border bg-status-info-subtle',
  PORT_ARRIVAL: 'border-status-info-border bg-status-info-subtle',
  PORT_DEPARTURE: 'border-status-info-border bg-status-info-subtle',

  CUSTOMS_CLEARED: 'border-status-info-border bg-status-info-subtle',
  SECURITY_CLEARED: 'border-status-info-border bg-status-info-subtle',
  INSPECTION_COMPLETE: 'border-status-info-border bg-status-info-subtle',

  DELIVERED: 'border-status-success-border bg-status-success-subtle',
  POD_RECEIVED: 'border-status-success-border bg-status-success-subtle',

  EXCEPTION: 'border-status-destructive-border bg-status-destructive-subtle',
  DELAYED: 'border-status-destructive-border bg-status-destructive-subtle',
  DAMAGE_REPORTED: 'border-status-destructive-border bg-status-destructive-subtle',

  DEFAULT: '',
} as const;

// ============================================
// WebSocket Connection Status
// ============================================

export const WEBSOCKET_STATUS_STYLES = {
  connected: 'bg-status-success-subtle text-status-success-foreground',
  connecting: 'bg-status-warning-subtle text-status-warning-foreground',
  disconnected: 'bg-status-destructive-subtle text-status-destructive-foreground',
  offline: 'bg-status-neutral-subtle text-status-neutral-foreground',
} as const;

export const WEBSOCKET_DOT_COLORS = {
  connected: 'bg-status-success',
  connecting: 'bg-status-warning',
  disconnected: 'bg-status-destructive',
  offline: 'bg-status-neutral',
} as const;

// ============================================
// Alert/Warning Colors
// ============================================

export const ALERT_STYLES = {
  info: 'bg-status-info-subtle border-status-info-border text-status-info-foreground',
  success: 'bg-status-success-subtle border-status-success-border text-status-success-foreground',
  warning: 'bg-status-warning-subtle border-status-warning-border text-status-warning-foreground',
  error:
    'bg-status-destructive-subtle border-status-destructive-border text-status-destructive-foreground',
} as const;

export const ALERT_ICON_COLORS = {
  info: 'text-status-info',
  success: 'text-status-success',
  warning: 'text-status-warning',
  error: 'text-status-destructive',
} as const;

// ============================================
// Transport Mode Icon Colors
// ============================================

/**
 * Uses the transport mode families rather than the status ones, so a mode
 * reads the same colour here as it does in <ModeBadge> across the tables.
 */
export const MODE_ICON_STYLES = {
  OCEAN: 'bg-mode-ocean-subtle text-mode-ocean-foreground',
  AIR: 'bg-mode-air-subtle text-mode-air-foreground',
  ROAD_FTL: 'bg-mode-truck-subtle text-mode-truck-foreground',
  ROAD_LTL: 'bg-mode-truck-subtle text-mode-truck-foreground',
  RAIL: 'bg-mode-rail-subtle text-mode-rail-foreground',
  // No dedicated family — multimodal spans several, so it stays neutral.
  MULTIMODAL: 'bg-status-neutral-subtle text-status-neutral-foreground',
  DEFAULT: 'bg-primary/10 text-primary',
} as const;

// ============================================
// Card & Container Styles
// ============================================

export const CARD_STYLES = {
  base: 'rounded-lg border bg-card shadow-sm',
  hover: 'hover:shadow-md transition-shadow',
  interactive: 'cursor-pointer hover:bg-accent transition-colors',
  selected: 'bg-accent border-primary',
} as const;

export const CONTAINER_STYLES = {
  panel: 'rounded-xl border shadow-sm overflow-hidden',
  section: 'space-y-4 p-4',
  page: 'p-6 space-y-6',
} as const;

// ============================================
// Helper Functions
// ============================================

/**
 * Get status badge style for a shipment status
 */
export function getShipmentStatusStyle(status: string): string {
  const normalizedStatus = status.toUpperCase().replace(/\s+/g, '_');
  return (
    SHIPMENT_STATUS_STYLES[normalizedStatus as keyof typeof SHIPMENT_STATUS_STYLES] ||
    SHIPMENT_STATUS_STYLES.DRAFT
  );
}

/**
 * Get timeline event color based on event type
 */
export function getTimelineEventColor(eventType: string): string {
  const normalizedType = eventType.toUpperCase().replace(/\s+/g, '_');
  return (
    TIMELINE_EVENT_COLORS[normalizedType as keyof typeof TIMELINE_EVENT_COLORS] ||
    TIMELINE_EVENT_COLORS.DEFAULT
  );
}

/**
 * Get timeline event border/background style
 */
export function getTimelineEventBorderStyle(eventType: string): string {
  const normalizedType = eventType.toUpperCase().replace(/\s+/g, '_');
  return (
    TIMELINE_EVENT_BORDER_COLORS[normalizedType as keyof typeof TIMELINE_EVENT_BORDER_COLORS] || ''
  );
}

/**
 * Get WebSocket connection status style
 */
export function getWebSocketStatusStyle(
  status: 'connected' | 'connecting' | 'disconnected' | 'offline'
): string {
  return WEBSOCKET_STATUS_STYLES[status] || WEBSOCKET_STATUS_STYLES.offline;
}

/**
 * Get WebSocket status dot color
 */
export function getWebSocketDotColor(
  status: 'connected' | 'connecting' | 'disconnected' | 'offline'
): string {
  return WEBSOCKET_DOT_COLORS[status] || WEBSOCKET_DOT_COLORS.offline;
}

/**
 * Get transport mode icon container style
 */
export function getModeIconStyle(mode: string): string {
  const normalizedMode = mode.toUpperCase().replace(/\s+/g, '_');
  return (
    MODE_ICON_STYLES[normalizedMode as keyof typeof MODE_ICON_STYLES] || MODE_ICON_STYLES.DEFAULT
  );
}

/**
 * Get alert style based on severity
 */
export function getAlertStyle(severity: 'info' | 'success' | 'warning' | 'error'): string {
  return ALERT_STYLES[severity];
}

/**
 * Get alert icon color
 */
export function getAlertIconColor(severity: 'info' | 'success' | 'warning' | 'error'): string {
  return ALERT_ICON_COLORS[severity];
}

// ============================================
// Typography Constants
// ============================================

export const TYPOGRAPHY = {
  pageTitle: 'text-2xl font-semibold tracking-tight',
  cardTitle: 'text-lg font-semibold',
  cardSubtitle: 'text-sm text-muted-foreground',
  sectionHeading: 'text-sm font-medium text-muted-foreground uppercase tracking-wide',
  body: 'text-sm',
  bodyMuted: 'text-sm text-muted-foreground',
  caption: 'text-xs text-muted-foreground',
  metric: 'text-3xl md:text-4xl font-semibold',
  label: 'text-sm font-medium',
} as const;

// ============================================
// Spacing Constants
// ============================================

export const SPACING = {
  cardPadding: 'p-6',
  cardContentPadding: 'p-6 pt-0',
  sectionGap: 'space-y-4',
  itemGap: 'space-y-2',
  iconGap: 'gap-2',
} as const;
